import React, { useState, useEffect } from 'react';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import axios from 'axios';
import { useAuth } from './AuthPages';
import { 
  LayoutDashboard, Image, FileText, MessageSquare, Users, 
  Plus, Edit, Trash2, Eye, EyeOff, Upload, X, Save,
  ChevronLeft, Menu, LogOut, Shield, UserPlus,
  Settings, ArrowUp, ArrowDown, AlertTriangle, BarChart3, Copy, Check
} from 'lucide-react';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
export const API = `${BACKEND_URL}/api`;

// Admin Layout
export const AdminLayout = ({ children }) => {
  const [sidebarOpen, setSidebarOpen] = useState(true);
  const location = useLocation();
  const { logout, user } = useAuth();
  const navigate = useNavigate();

  const handleLogout = async () => {
    await logout();
    navigate('/admin/login');
  };

  const menuItems = [
    { name: 'Dashboard', path: '/admin', icon: LayoutDashboard },
    { name: 'Galeria', path: '/admin/gallery', icon: Image },
    { name: 'Blog', path: '/admin/blog', icon: FileText },
    { name: 'Contatos', path: '/admin/contacts', icon: MessageSquare },
    { name: 'Estatísticas', path: '/admin/analytics', icon: BarChart3 },
    { name: 'Depoimentos', path: '/admin/testimonials', icon: Users },
    { name: 'Usuários', path: '/admin/users', icon: Shield },
    { name: 'Configurações', path: '/admin/settings', icon: Settings },
  ];

  return (
    <div className="flex min-h-screen bg-[#030712]" data-testid="admin-layout">
      {/* Sidebar */}
      <aside className={`${sidebarOpen ? 'w-64' : 'w-20'} admin-sidebar transition-all duration-300 flex flex-col`}>
        <div className="p-6 flex items-center gap-3">
          <div className="text-xl font-black">
            <span className="text-white">TO</span>
            <span className="gradient-text">+</span>
            <span className="text-white">L</span>
          </div>
          {sidebarOpen && <span className="text-sm text-gray-400">Painel</span>}
        </div>

        <nav className="flex-1 px-4 space-y-2">
          {menuItems.map((item) => {
            const isActive = location.pathname === item.path;
            return (
              <Link
                key={item.path}
                to={item.path}
                className={`flex items-center gap-3 px-4 py-3 rounded-lg transition-all ${
                  isActive 
                    ? 'bg-[#0055FF] text-white' 
                    : 'text-gray-400 hover:bg-white/5 hover:text-white'
                }`}
                data-testid={`admin-nav-${item.name.toLowerCase()}`}
              >
                <item.icon size={20} />
                {sidebarOpen && <span className="font-medium">{item.name}</span>}
              </Link>
            );
          })}
        </nav>

        <div className="p-4 border-t border-white/5">
          <Link
            to="/"
            className="flex items-center gap-3 px-4 py-3 text-gray-400 hover:text-white transition-colors"
            data-testid="admin-back-to-site"
          >
            <ChevronLeft size={20} />
            {sidebarOpen && <span>Voltar ao site</span>}
          </Link>
        </div>
      </aside>

      {/* Main Content */}
      <main className="flex-1 overflow-auto">
        <header className="glass-header sticky top-0 z-10 px-8 py-4 flex items-center justify-between">
          <button
            onClick={() => setSidebarOpen(!sidebarOpen)}
            className="text-gray-400 hover:text-white transition-colors"
            data-testid="toggle-sidebar"
          >
            <Menu size={24} />
          </button>
          <div className="flex items-center gap-4">
            <span className="text-sm text-gray-400">{user?.email || 'Grupo Tomaselli'}</span>
            <button onClick={handleLogout} className="flex items-center gap-2 text-gray-400 hover:text-red-400 transition-colors text-sm" data-testid="admin-logout-btn">
              <LogOut size={18} /> Sair
            </button>
          </div>
        </header>

        <div className="p-8">
          {children}
        </div>
      </main>
    </div>
  );
};

// Dashboard
export const AdminDashboard = () => {
  const [stats, setStats] = useState({
    contacts: 0,
    posts: 0,
    events: 0,
    testimonials: 0
  });
  const [recentContacts, setRecentContacts] = useState([]);

  useEffect(() => {
    fetchStats();
    fetchRecentContacts();
  }, []);

  const fetchStats = async () => {
    try {
      const [contacts, posts, events, testimonials] = await Promise.all([
        axios.get(`${API}/contacts`),
        axios.get(`${API}/blog`),
        axios.get(`${API}/gallery`),
        axios.get(`${API}/testimonials`)
      ]);
      setStats({
        contacts: contacts.data.length,
        posts: posts.data.length,
        events: events.data.length,
        testimonials: testimonials.data.length
      });
    } catch (error) {
      console.error('Error fetching stats:', error);
    }
  };

  const fetchRecentContacts = async () => {
    try {
      const response = await axios.get(`${API}/contacts`);
      setRecentContacts(response.data.slice(0, 5));
    } catch (error) {
      console.error('Error fetching contacts:', error);
    }
  };

  const statCards = [
    { name: 'Contatos', value: stats.contacts, icon: MessageSquare, color: 'from-blue-500 to-cyan-500', link: '/admin/contacts' },
    { name: 'Posts', value: stats.posts, icon: FileText, color: 'from-purple-500 to-pink-500', link: '/admin/blog' },
    { name: 'Galeria', value: stats.events, icon: Image, color: 'from-green-500 to-emerald-500', link: '/admin/gallery' },
    { name: 'Depoimentos', value: stats.testimonials, icon: Users, color: 'from-amber-500 to-orange-500', link: '/admin/testimonials' },
  ];

  return (
    <AdminLayout>
      <div data-testid="admin-dashboard">
        <h1 className="text-3xl font-bold text-white mb-2">Dashboard</h1>
        <p className="text-gray-400 mb-8">Bem-vindo ao painel de gestão do Grupo Tomaselli</p>
        
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6 mb-8">
          {statCards.map((stat) => (
            <Link key={stat.name} to={stat.link} className="admin-card p-6 hover:border-white/20 transition-colors" data-testid={`stat-${stat.name.toLowerCase()}`}>
              <div className={`w-12 h-12 rounded-lg bg-gradient-to-br ${stat.color} flex items-center justify-center mb-4`}>
                <stat.icon className="text-white" size={24} />
              </div>
              <p className="text-3xl font-bold text-white">{stat.value}</p>
              <p className="text-gray-400 text-sm">{stat.name}</p>
            </Link>
          ))}
        </div>

        {/* Recent Contacts */}
        <div className="admin-card p-6">
          <div className="flex items-center justify-between mb-4">
            <h2 className="text-xl font-bold text-white">Contatos Recentes</h2>
            <Link to="/admin/contacts" className="text-[#0055FF] text-sm hover:underline">Ver todos</Link>
          </div>
          {recentContacts.length > 0 ? (
            <div className="space-y-3">
              {recentContacts.map((contact) => (
                <div key={contact.id} className="flex items-center justify-between p-3 bg-white/5 rounded-lg">
                  <div>
                    <p className="text-white font-medium">{contact.name}</p>
                    <p className="text-gray-500 text-sm">{contact.service || 'Não especificado'}</p>
                  </div>
                  <div className="text-right">
                    <p className="text-gray-400 text-sm">{new Date(contact.created_at).toLocaleDateString('pt-BR')}</p>
                    <span className={`text-xs px-2 py-1 rounded ${
                      contact.status === 'new' ? 'bg-blue-500/20 text-blue-400' :
                      contact.status === 'contacted' ? 'bg-yellow-500/20 text-yellow-400' :
                      contact.status === 'converted' ? 'bg-green-500/20 text-green-400' :
                      'bg-gray-500/20 text-gray-400'
                    }`}>
                      {contact.status === 'new' ? 'Novo' : contact.status === 'contacted' ? 'Contatado' : contact.status === 'converted' ? 'Convertido' : 'Fechado'}
                    </span>
                  </div>
                </div>
              ))}
            </div>
          ) : (
            <p className="text-gray-500 text-center py-4">Nenhum contato recente</p>
          )}
        </div>
      </div>
    </AdminLayout>
  );
};

// Gallery Management
export const AdminGallery = () => {
  const [events, setEvents] = useState([]);
  const [showForm, setShowForm] = useState(false);
  const [editingEvent, setEditingEvent] = useState(null);
  const [formData, setFormData] = useState({
    title: '',
    category: '',
    description: '',
    date: '',
    featured: false
  });
  const [pendingFiles, setPendingFiles] = useState([]);

  useEffect(() => {
    fetchEvents();
  }, []);

  const fetchEvents = async () => {
    try {
      const response = await axios.get(`${API}/gallery`);
      setEvents(response.data);
    } catch (error) {
      console.error('Error fetching events:', error);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    try {
      let eventId;
      if (editingEvent) {
        await axios.put(`${API}/gallery/${editingEvent.id}`, formData);
        eventId = editingEvent.id;
      } else {
        const res = await axios.post(`${API}/gallery`, formData);
        eventId = res.data.id;
      }
      // Upload pending files if any
      if (pendingFiles.length > 0 && eventId) {
        const uploadData = new FormData();
        for (const file of pendingFiles) {
          uploadData.append('files', file);
        }
        await axios.post(`${API}/gallery/${eventId}/images`, uploadData, {
          headers: { 'Content-Type': 'multipart/form-data' }
        });
      }
      fetchEvents();
      resetForm();
    } catch (error) {
      console.error('Error saving event:', error);
    }
  };

  const [deleteConfirm, setDeleteConfirm] = useState(null);

  const handleDelete = async (id) => {
    try {
      await axios.delete(`${API}/gallery/${id}`);
      setDeleteConfirm(null);
      fetchEvents();
    } catch (error) {
      console.error('Error deleting event:', error);
    }
  };

  const handleEdit = (event) => {
    setEditingEvent(event);
    setFormData({
      title: event.title,
      category: event.category,
      description: event.description || '',
      date: event.date || '',
      featured: event.featured
    });
    setShowForm(true);
  };

  const resetForm = () => {
    setShowForm(false);
    setEditingEvent(null);
    setFormData({ title: '', category: '', description: '', date: '', featured: false });
    setPendingFiles([]);
  };

  const handleImageUpload = async (eventId, files) => {
    const formData = new FormData();
    for (const file of files) {
      formData.append('files', file);
    }
    try {
      await axios.post(`${API}/gallery/${eventId}/images`, formData, {
        headers: { 'Content-Type': 'multipart/form-data' }
      });
      fetchEvents();
    } catch (error) {
      console.error('Error uploading images:', error);
    }
  };

  return (
    <AdminLayout>
      <div data-testid="admin-gallery">
        <div className="flex items-center justify-between mb-8">
          <div>
            <h1 className="text-3xl font-bold text-white">Galeria de Eventos</h1>
            <p className="text-gray-400">Gerencie as fotos e eventos do portfólio</p>
          </div>
          <button
            onClick={() => setShowForm(true)}
            className="btn-primary flex items-center gap-2"
            data-testid="add-event-btn"
          >
            <Plus size={18} /> Novo Evento
          </button>
        </div>

        {/* Form Modal */}
        {showForm && (
          <div className="fixed inset-0 bg-black/80 flex items-center justify-center z-50 p-4">
            <div className="admin-card w-full max-w-lg p-8" data-testid="event-form">
              <div className="flex items-center justify-between mb-6">
                <h2 className="text-xl font-bold text-white">
                  {editingEvent ? 'Editar Evento' : 'Novo Evento'}
                </h2>
                <button onClick={resetForm} className="text-gray-400 hover:text-white">
                  <X size={24} />
                </button>
              </div>

              <form onSubmit={handleSubmit} className="space-y-4">
                <input
                  type="text"
                  placeholder="Título do evento *"
                  required
                  value={formData.title}
                  onChange={(e) => setFormData({ ...formData, title: e.target.value })}
                  className="input-dark w-full"
                  data-testid="event-title"
                />

                <select
                  required
                  value={formData.category}
                  onChange={(e) => setFormData({ ...formData, category: e.target.value })}
                  className="input-dark w-full bg-transparent"
                  data-testid="event-category"
                >
                  <option value="" className="bg-[#0B1221]">Categoria *</option>
                  <option value="Shows" className="bg-[#0B1221]">Shows</option>
                  <option value="Corporativo" className="bg-[#0B1221]">Corporativo</option>
                  <option value="Convenções" className="bg-[#0B1221]">Convenções</option>
                  <option value="Feiras" className="bg-[#0B1221]">Feiras</option>
                  <option value="Confraternizações" className="bg-[#0B1221]">Confraternizações</option>
                  <option value="Teatros" className="bg-[#0B1221]">Teatros/Espetáculos</option>
                </select>

                <textarea
                  placeholder="Descrição do evento"
                  rows={3}
                  value={formData.description}
                  onChange={(e) => setFormData({ ...formData, description: e.target.value })}
                  className="input-dark w-full resize-none"
                  data-testid="event-description"
                />

                <input
                  type="date"
                  value={formData.date}
                  onChange={(e) => setFormData({ ...formData, date: e.target.value })}
                  className="input-dark w-full"
                  data-testid="event-date"
                />

                <div>
                  <label className="flex items-center gap-3 cursor-pointer p-3 border border-dashed border-white/20 rounded-xl hover:border-blue-500/50 transition-colors">
                    <Upload size={20} className="text-gray-400" />
                    <span className="text-gray-300 text-sm">
                      {pendingFiles.length > 0 
                        ? `${pendingFiles.length} foto(s) selecionada(s)` 
                        : 'Selecionar fotos do evento'}
                    </span>
                    <input
                      type="file"
                      multiple
                      accept="image/*"
                      className="hidden"
                      onChange={(e) => setPendingFiles(Array.from(e.target.files))}
                      data-testid="event-photos"
                    />
                  </label>
                  {pendingFiles.length > 0 && (
                    <div className="flex gap-2 mt-2 flex-wrap">
                      {pendingFiles.map((f, i) => (
                        <span key={i} className="text-xs bg-white/5 text-gray-400 px-2 py-1 rounded">{f.name}</span>
                      ))}
                    </div>
                  )}
                </div>

                <label className="flex items-center gap-3 cursor-pointer">
                  <input
                    type="checkbox"
                    checked={formData.featured}
                    onChange={(e) => setFormData({ ...formData, featured: e.target.checked })}
                    className="w-5 h-5 rounded"
                    data-testid="event-featured"
                  />
                  <span className="text-gray-300">Destacar na página inicial</span>
                </label>

                <div className="flex gap-4 pt-4">
                  <button type="button" onClick={resetForm} className="btn-outline flex-1">
                    Cancelar
                  </button>
                  <button type="submit" className="btn-primary flex-1 flex items-center justify-center gap-2" data-testid="save-event-btn">
                    <Save size={18} /> Salvar
                  </button>
                </div>
              </form>
            </div>
          </div>
        )}

        {/* Events Grid */}
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
          {events.map((event) => (
            <div key={event.id} className="admin-card overflow-hidden" data-testid={`gallery-event-${event.id}`}>
              <div className="aspect-video relative bg-gradient-to-br from-[#0055FF]/20 to-[#00E5FF]/10">
                {event.images?.length > 0 ? (
                  <img
                    src={event.images[0].startsWith('/') ? `${BACKEND_URL}${event.images[0]}` : event.images[0]}
                    alt={event.title}
                    className="w-full h-full object-cover"
                  />
                ) : (
                  <div className="w-full h-full flex items-center justify-center">
                    <Image className="text-gray-600" size={48} />
                  </div>
                )}
                {event.featured && (
                  <span className="absolute top-2 right-2 bg-[#0055FF] text-white text-xs px-2 py-1 rounded">
                    Destaque
                  </span>
                )}
              </div>
              
              <div className="p-4">
                <span className="text-xs text-cyan-400 uppercase tracking-wider">{event.category}</span>
                <h3 className="text-lg font-bold text-white mt-1">{event.title}</h3>
                {event.description && <p className="text-sm text-gray-400 mt-1 line-clamp-2">{event.description}</p>}
                
                <div className="flex items-center gap-2 mt-4">
                  <label className="flex-1 cursor-pointer">
                    <input
                      type="file"
                      multiple
                      accept="image/*"
                      className="hidden"
                      onChange={(e) => handleImageUpload(event.id, e.target.files)}
                    />
                    <span className="flex items-center justify-center gap-2 px-3 py-2 bg-white/5 text-gray-400 hover:bg-white/10 hover:text-white transition-colors rounded text-sm">
                      <Upload size={16} /> Adicionar Fotos
                    </span>
                  </label>
                  <button onClick={() => handleEdit(event)} className="p-2 text-gray-400 hover:text-white transition-colors" data-testid={`edit-event-${event.id}`}>
                    <Edit size={18} />
                  </button>
                  <button onClick={() => setDeleteConfirm(event.id)} className="p-2 text-gray-400 hover:text-red-400 transition-colors" data-testid={`delete-event-${event.id}`}>
                    <Trash2 size={18} />
                  </button>
                </div>

                {event.images?.length > 0 && (
                  <div className="flex gap-2 mt-4 overflow-x-auto pb-2">
                    {event.images.map((img, idx) => (
                      <img
                        key={idx}
                        src={img.startsWith('/') ? `${BACKEND_URL}${img}` : img}
                        alt={`${event.title} ${idx + 1}`}
                        className="w-16 h-16 object-cover rounded flex-shrink-0 border border-white/10"
                      />
                    ))}
                  </div>
                )}
              </div>
            </div>
          ))}
        </div>

        {events.length === 0 && (
          <div className="text-center py-16 admin-card">
            <Image className="text-gray-600 mx-auto mb-4" size={48} />
            <p className="text-gray-400 mb-4">Nenhum evento cadastrado</p>
            <button onClick={() => setShowForm(true)} className="btn-primary">
              Adicionar primeiro evento
            </button>
          </div>
        )}

        {deleteConfirm && (
          <div className="fixed inset-0 bg-black/60 backdrop-blur-sm z-50 flex items-center justify-center p-4">
            <div className="admin-card p-6 max-w-sm w-full text-center">
              <Trash2 className="text-red-400 mx-auto mb-3" size={32} />
              <h3 className="text-white font-bold text-lg mb-2">Excluir evento?</h3>
              <p className="text-gray-400 text-sm mb-6">Esta ação não pode ser desfeita.</p>
              <div className="flex gap-3">
                <button onClick={() => setDeleteConfirm(null)} className="btn-outline flex-1">Cancelar</button>
                <button onClick={() => handleDelete(deleteConfirm)} className="flex-1 bg-red-500 hover:bg-red-600 text-white py-2.5 px-4 rounded-xl font-semibold transition-colors" data-testid="confirm-delete-btn">Excluir</button>
              </div>
            </div>
          </div>
        )}
      </div>
    </AdminLayout>
  );
};

// Blog Management
export const AdminBlog = () => {
  const [posts, setPosts] = useState([]);
  const [showForm, setShowForm] = useState(false);
  const [editingPost, setEditingPost] = useState(null);
  const [uploading, setUploading] = useState(false);
  const [imagePreview, setImagePreview] = useState('');
  const [formData, setFormData] = useState({
    title: '',
    slug: '',
    excerpt: '',
    content: '',
    image_url: '',
    published: false
  });

  useEffect(() => { fetchPosts(); }, []);

  const fetchPosts = async () => {
    try {
      const response = await axios.get(`${API}/blog`);
      setPosts(response.data);
    } catch (error) {
      console.error('Error fetching posts:', error);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    try {
      if (editingPost) {
        await axios.put(`${API}/blog/${editingPost.id}`, formData);
      } else {
        await axios.post(`${API}/blog`, formData);
      }
      fetchPosts();
      resetForm();
    } catch (error) {
      console.error('Error saving post:', error);
    }
  };

  const [deleteConfirm, setDeleteConfirm] = useState(null);

  const handleDelete = async (id) => {
    try {
      await axios.delete(`${API}/blog/${id}`);
      setDeleteConfirm(null);
      fetchPosts();
    } catch (error) {
      console.error('Error deleting post:', error);
    }
  };

  const handleEdit = (post) => {
    setEditingPost(post);
    setFormData({
      title: post.title,
      slug: post.slug,
      excerpt: post.excerpt,
      content: post.content,
      image_url: post.image_url || '',
      published: post.published
    });
    setImagePreview(post.image_url ? (post.image_url.startsWith('/') ? `${BACKEND_URL}${post.image_url}` : post.image_url) : '');
    setShowForm(true);
  };

  const togglePublished = async (post) => {
    try {
      await axios.put(`${API}/blog/${post.id}`, { published: !post.published });
      fetchPosts();
    } catch (error) {
      console.error('Error toggling published:', error);
    }
  };

  const resetForm = () => {
    setShowForm(false);
    setEditingPost(null);
    setImagePreview('');
    setFormData({ title: '', slug: '', excerpt: '', content: '', image_url: '', published: false });
  };

  const handleImageUpload = async (e) => {
    const file = e.target.files[0];
    if (!file) return;
    setUploading(true);
    try {
      const uploadData = new FormData();
      uploadData.append('file', file);
      const token = localStorage.getItem('token');
      const res = await axios.post(`${API}/upload/image`, uploadData, {
        headers: { 'Content-Type': 'multipart/form-data', Authorization: `Bearer ${token}` }
      });
      setFormData(prev => ({ ...prev, image_url: res.data.url }));
      setImagePreview(`${BACKEND_URL}${res.data.url}`);
    } catch (error) {
      console.error('Erro ao enviar imagem:', error);
    }
    setUploading(false);
  };

  const generateSlug = (title) => {
    return title.toLowerCase().normalize('NFD').replace(/[\u0300-\u036f]/g, '').replace(/[^a-z0-9]+/g, '-').replace(/(^-|-$)/g, '');
  };

  return (
    <AdminLayout>
      <div data-testid="admin-blog">
        <div className="flex items-center justify-between mb-8">
          <div>
            <h1 className="text-3xl font-bold text-white">Blog</h1>
            <p className="text-gray-400">Gerencie os artigos e publicações</p>
          </div>
          <button onClick={() => setShowForm(true)} className="btn-primary flex items-center gap-2" data-testid="add-post-btn">
            <Plus size={18} /> Novo Post
          </button>
        </div>

        {/* Form Modal */}
        {showForm && (
          <div className="fixed inset-0 bg-black/80 flex items-center justify-center z-50 p-4 overflow-auto">
            <div className="admin-card w-full max-w-2xl p-8 my-8" data-testid="post-form">
              <div className="flex items-center justify-between mb-6">
                <h2 className="text-xl font-bold text-white">{editingPost ? 'Editar Post' : 'Novo Post'}</h2>
                <button onClick={resetForm} className="text-gray-400 hover:text-white"><X size={24} /></button>
              </div>

              <form onSubmit={handleSubmit} className="space-y-4">
                <input type="text" placeholder="Título do post *" required value={formData.title}
                  onChange={(e) => {
                    const title = e.target.value;
                    setFormData({ ...formData, title, slug: editingPost ? formData.slug : generateSlug(title) });
                  }}
                  className="input-dark w-full" data-testid="post-title" />

                <input type="text" placeholder="URL amigável (slug) *" required value={formData.slug}
                  onChange={(e) => setFormData({ ...formData, slug: e.target.value })}
                  className="input-dark w-full" data-testid="post-slug" />

                <div>
                  <label className="block text-gray-400 text-sm mb-2">Imagem de capa</label>
                  <div className="flex items-center gap-4">
                    <label className={`flex items-center gap-2 px-4 py-2.5 rounded-lg cursor-pointer transition-colors ${uploading ? 'bg-gray-700 text-gray-500' : 'bg-[#00A8E8]/20 text-[#00A8E8] hover:bg-[#00A8E8]/30 border border-[#00A8E8]/30'}`}>
                      <Upload size={18} />
                      <span className="text-sm font-medium">{uploading ? 'Enviando...' : 'Anexar Foto'}</span>
                      <input type="file" accept="image/*" onChange={handleImageUpload} disabled={uploading} className="hidden" data-testid="post-image-upload" />
                    </label>
                    {imagePreview && (
                      <div className="relative">
                        <img src={imagePreview} alt="Preview" className="h-16 w-24 object-cover rounded-lg border border-gray-600" />
                        <button type="button" onClick={() => { setImagePreview(''); setFormData(prev => ({...prev, image_url: ''})); }}
                          className="absolute -top-2 -right-2 bg-red-500 rounded-full p-0.5 hover:bg-red-400">
                          <X size={14} className="text-white" />
                        </button>
                      </div>
                    )}
                  </div>
                </div>

                <textarea placeholder="Resumo (aparece na listagem) *" required rows={2} value={formData.excerpt}
                  onChange={(e) => setFormData({ ...formData, excerpt: e.target.value })}
                  className="input-dark w-full resize-none" data-testid="post-excerpt" />

                <textarea placeholder="Conteúdo completo do post *" required rows={10} value={formData.content}
                  onChange={(e) => setFormData({ ...formData, content: e.target.value })}
                  className="input-dark w-full resize-none" data-testid="post-content" />

                <label className="flex items-center gap-3 cursor-pointer">
                  <input type="checkbox" checked={formData.published}
                    onChange={(e) => setFormData({ ...formData, published: e.target.checked })}
                    className="w-5 h-5 rounded" data-testid="post-published" />
                  <span className="text-gray-300">Publicar imediatamente</span>
                </label>

                <div className="flex gap-4 pt-4">
                  <button type="button" onClick={resetForm} className="btn-outline flex-1">Cancelar</button>
                  <button type="submit" className="btn-primary flex-1 flex items-center justify-center gap-2" data-testid="save-post-btn">
                    <Save size={18} /> Salvar
                  </button>
                </div>
              </form>
            </div>
          </div>
        )}

        {/* Posts Table */}
        <div className="admin-card overflow-hidden">
          <table className="data-table" data-testid="posts-table">
            <thead>
              <tr>
                <th>Título</th>
                <th>Data</th>
                <th>Status</th>
                <th>Ações</th>
              </tr>
            </thead>
            <tbody>
              {posts.map((post) => (
                <tr key={post.id} data-testid={`post-row-${post.id}`}>
                  <td>
                    <div>
                      <p className="font-medium text-white">{post.title}</p>
                      <p className="text-sm text-gray-500">/{post.slug}</p>
                    </div>
                  </td>
                  <td className="text-gray-400">{new Date(post.created_at).toLocaleDateString('pt-BR')}</td>
                  <td>
                    <button onClick={() => togglePublished(post)}
                      className={`flex items-center gap-2 px-3 py-1 rounded text-sm ${
                        post.published ? 'bg-green-500/20 text-green-400' : 'bg-yellow-500/20 text-yellow-400'
                      }`} data-testid={`toggle-publish-${post.id}`}>
                      {post.published ? <Eye size={14} /> : <EyeOff size={14} />}
                      {post.published ? 'Publicado' : 'Rascunho'}
                    </button>
                  </td>
                  <td>
                    <div className="flex items-center gap-2">
                      <button onClick={() => handleEdit(post)} className="p-2 text-gray-400 hover:text-white transition-colors" data-testid={`edit-post-${post.id}`}>
                        <Edit size={18} />
                      </button>
                      <button onClick={() => setDeleteConfirm(post.id)} className="p-2 text-gray-400 hover:text-red-400 transition-colors" data-testid={`delete-post-${post.id}`}>
                        <Trash2 size={18} />
                      </button>
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>

          {posts.length === 0 && (
            <div className="text-center py-16">
              <FileText className="text-gray-600 mx-auto mb-4" size={48} />
              <p className="text-gray-400 mb-4">Nenhum post cadastrado</p>
              <button onClick={() => setShowForm(true)} className="btn-primary">Criar primeiro post</button>
            </div>
          )}
        </div>

        {deleteConfirm && (
          <div className="fixed inset-0 bg-black/60 backdrop-blur-sm z-50 flex items-center justify-center p-4">
            <div className="admin-card p-6 max-w-sm w-full text-center">
              <Trash2 className="text-red-400 mx-auto mb-3" size={32} />
              <h3 className="text-white font-bold text-lg mb-2">Excluir post?</h3>
              <p className="text-gray-400 text-sm mb-6">Esta ação não pode ser desfeita.</p>
              <div className="flex gap-3">
                <button onClick={() => setDeleteConfirm(null)} className="btn-outline flex-1">Cancelar</button>
                <button onClick={() => handleDelete(deleteConfirm)} className="flex-1 bg-red-500 hover:bg-red-600 text-white py-2.5 px-4 rounded-xl font-semibold transition-colors" data-testid="confirm-delete-btn">Excluir</button>
              </div>
            </div>
          </div>
        )}
      </div>
    </AdminLayout>
  );
};

// Contacts Management
export const AdminContacts = () => {
  const [contacts, setContacts] = useState([]);
  const [selectedContact, setSelectedContact] = useState(null);
  const [filterStatus, setFilterStatus] = useState('all');
  const [copied, setCopied] = useState(false);

  const copyContact = (c) => {
    const lines = [
      `Nome: ${c.name}`, `Email: ${c.email}`, `Telefone: ${c.phone || '-'}`,
      c.service && `Serviço: ${c.service}`,
      c.event_date && `Data do Evento: ${c.event_date}`,
      c.event_location && `Local: ${c.event_location}`,
      c.guests && `Nº de Pessoas: ${c.guests}`,
      '', 'Mensagem:', c.message,
    ].filter(Boolean);
    const txt = lines.join('\n');
    const done = () => { setCopied(true); setTimeout(() => setCopied(false), 2000); };
    if (navigator.clipboard?.writeText) {
      navigator.clipboard.writeText(txt).then(done).catch(() => fallbackCopy(txt, done));
    } else fallbackCopy(txt, done);
  };

  const fallbackCopy = (txt, done) => {
    const ta = document.createElement('textarea');
    ta.value = txt;
    ta.style.position = 'fixed';
    document.body.appendChild(ta);
    ta.select();
    document.execCommand('copy');
    document.body.removeChild(ta);
    done();
  };

  useEffect(() => { fetchContacts(); }, []);

  const fetchContacts = async () => {
    try {
      const response = await axios.get(`${API}/contacts`);
      setContacts(response.data);
    } catch (error) {
      console.error('Error fetching contacts:', error);
    }
  };

  const updateStatus = async (id, status) => {
    try {
      await axios.patch(`${API}/contacts/${id}/status?status=${status}`);
      fetchContacts();
    } catch (error) {
      console.error('Error updating status:', error);
    }
  };

  const statusColors = {
    new: 'bg-blue-500/20 text-blue-400',
    contacted: 'bg-yellow-500/20 text-yellow-400',
    converted: 'bg-green-500/20 text-green-400',
    closed: 'bg-gray-500/20 text-gray-400'
  };

  const statusLabels = { new: 'Novo', contacted: 'Contatado', converted: 'Convertido', closed: 'Fechado' };

  const filteredContacts = filterStatus === 'all' ? contacts : contacts.filter(c => c.status === filterStatus);
  const emailFailures = contacts.filter(c => c.email_sent === false);

  return (
    <AdminLayout>
      <div data-testid="admin-contacts">
        {emailFailures.length > 0 && (
          <div className="flex items-start gap-3 bg-red-500/10 border border-red-500/30 rounded-lg p-4 mb-6" data-testid="email-failure-alert">
            <AlertTriangle className="text-red-400 flex-shrink-0 mt-0.5" size={20} />
            <div>
              <p className="text-red-400 font-semibold">Atenção: falha no envio de e-mail</p>
              <p className="text-gray-300 text-sm mt-1">
                {emailFailures.length} solicitaç{emailFailures.length === 1 ? 'ão foi salva' : 'ões foram salvas'} mas o e-mail de notificação NÃO foi enviado. Os dados estão seguros abaixo (linhas marcadas com <AlertTriangle className="inline text-red-400" size={13} />). Verifique a configuração da Resend.
              </p>
            </div>
          </div>
        )}
        <div className="flex items-center justify-between mb-8">
          <div>
            <h1 className="text-3xl font-bold text-white">Solicitações de Contato</h1>
            <p className="text-gray-400">Gerencie os leads e solicitações de orçamento</p>
          </div>
          <div className="flex gap-2">
            {['all', 'new', 'contacted', 'converted', 'closed'].map((status) => (
              <button key={status} onClick={() => setFilterStatus(status)}
                className={`px-4 py-2 rounded-full text-sm transition-colors ${
                  filterStatus === status ? 'bg-[#0055FF] text-white' : 'bg-white/5 text-gray-400 hover:bg-white/10'
                }`}>
                {status === 'all' ? 'Todos' : statusLabels[status]}
              </button>
            ))}
          </div>
        </div>

        {/* Contact Detail Modal */}
        {selectedContact && (
          <div className="fixed inset-0 bg-black/80 flex items-center justify-center z-50 p-4">
            <div className="admin-card w-full max-w-lg p-8 max-h-[88vh] overflow-y-auto" data-testid="contact-detail">
              <div className="flex items-center justify-between mb-6 sticky -top-8 bg-[#0d1220] py-2 -my-2 z-10">
                <h2 className="text-xl font-bold text-white">Detalhes do Contato</h2>
                <div className="flex items-center gap-2">
                  <button onClick={() => copyContact(selectedContact)} data-testid="copy-contact-btn"
                    className="flex items-center gap-1.5 px-3 py-1.5 rounded bg-white/10 hover:bg-white/20 text-gray-200 text-sm transition-colors">
                    {copied ? <Check size={15} className="text-green-400" /> : <Copy size={15} />}
                    {copied ? 'Copiado!' : 'Copiar tudo'}
                  </button>
                  <button onClick={() => setSelectedContact(null)} className="text-gray-400 hover:text-white"><X size={24} /></button>
                </div>
              </div>

              <div className="space-y-4">
                <div><p className="text-sm text-gray-500">Nome</p><p className="text-white font-medium">{selectedContact.name}</p></div>
                <div><p className="text-sm text-gray-500">Email</p><p className="text-white">{selectedContact.email}</p></div>
                <div><p className="text-sm text-gray-500">Telefone</p><p className="text-white">{selectedContact.phone}</p></div>
                {selectedContact.service && <div><p className="text-sm text-gray-500">Serviço</p><p className="text-white">{selectedContact.service}</p></div>}
                {selectedContact.event_date && <div><p className="text-sm text-gray-500">Data do Evento</p><p className="text-white">{selectedContact.event_date}</p></div>}
                {selectedContact.event_location && <div><p className="text-sm text-gray-500">Local</p><p className="text-white">{selectedContact.event_location}</p></div>}
                {selectedContact.guests && <div><p className="text-sm text-gray-500">Nº de Pessoas</p><p className="text-white">{selectedContact.guests}</p></div>}
                <div><p className="text-sm text-gray-500">Mensagem</p><p className="text-gray-300 whitespace-pre-wrap bg-white/5 p-3 rounded">{selectedContact.message}</p></div>
                {selectedContact.email_sent === false && (
                  <div className="bg-red-500/10 border border-red-500/30 rounded p-3" data-testid="contact-email-error">
                    <p className="text-red-400 text-sm font-semibold flex items-center gap-2"><AlertTriangle size={14} /> E-mail de notificação falhou</p>
                    {selectedContact.email_error && <p className="text-gray-400 text-xs mt-1 break-words">{selectedContact.email_error}</p>}
                  </div>
                )}

                <div className="pt-4 border-t border-white/5">
                  <p className="text-sm text-gray-500 mb-3">Atualizar Status</p>
                  <div className="flex flex-wrap gap-2">
                    {Object.entries(statusLabels).map(([key, label]) => (
                      <button key={key}
                        onClick={() => { updateStatus(selectedContact.id, key); setSelectedContact({ ...selectedContact, status: key }); }}
                        className={`px-4 py-2 rounded text-sm transition-colors ${
                          selectedContact.status === key ? statusColors[key] : 'bg-white/5 text-gray-400 hover:bg-white/10'
                        }`}>{label}</button>
                    ))}
                  </div>
                </div>
              </div>
            </div>
          </div>
        )}

        {/* Contacts Table */}
        <div className="admin-card overflow-hidden overflow-x-auto">
          <table className="data-table" data-testid="contacts-table">
            <thead>
              <tr><th>Nome</th><th>Contato</th><th>Serviço</th><th>Data</th><th>Status</th><th>Ações</th></tr>
            </thead>
            <tbody>
              {filteredContacts.map((contact) => (
                <tr key={contact.id} data-testid={`contact-row-${contact.id}`}>
                  <td>
                    <p className="font-medium text-white flex items-center gap-2">
                      {contact.email_sent === false && <AlertTriangle className="text-red-400 flex-shrink-0" size={16} data-testid={`email-fail-icon-${contact.id}`} title="E-mail de notificação falhou" />}
                      {contact.name}
                    </p>
                  </td>
                  <td><p className="text-gray-400">{contact.email}</p><p className="text-sm text-gray-500">{contact.phone}</p></td>
                  <td className="text-gray-400">{contact.service || '-'}</td>
                  <td className="text-gray-400">{new Date(contact.created_at).toLocaleDateString('pt-BR')}</td>
                  <td><span className={`px-3 py-1 rounded text-sm ${statusColors[contact.status]}`}>{statusLabels[contact.status]}</span></td>
                  <td>
                    <button onClick={() => setSelectedContact(contact)} className="p-2 text-gray-400 hover:text-white transition-colors" data-testid={`view-contact-${contact.id}`}>
                      <Eye size={18} />
                    </button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>

          {filteredContacts.length === 0 && (
            <div className="text-center py-16">
              <MessageSquare className="text-gray-600 mx-auto mb-4" size={48} />
              <p className="text-gray-400">Nenhuma solicitação encontrada</p>
            </div>
          )}
        </div>
      </div>
    </AdminLayout>
  );
};

// Testimonials Management
export const AdminTestimonials = () => {
  const [testimonials, setTestimonials] = useState([]);
  const [showForm, setShowForm] = useState(false);
  const [uploading, setUploading] = useState(false);
  const [imagePreview, setImagePreview] = useState('');
  const [formData, setFormData] = useState({ name: '', company: '', role: '', content: '', source: '', image_url: '', featured: true });

  useEffect(() => { fetchTestimonials(); }, []);

  const fetchTestimonials = async () => {
    try {
      const response = await axios.get(`${API}/testimonials`);
      setTestimonials(response.data);
    } catch (error) {
      console.error('Error fetching testimonials:', error);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    try {
      await axios.post(`${API}/testimonials`, formData);
      fetchTestimonials();
      resetForm();
    } catch (error) {
      console.error('Error saving testimonial:', error);
    }
  };

  const [deleteConfirm, setDeleteConfirm] = useState(null);

  const handleDelete = async (id) => {
    try {
      await axios.delete(`${API}/testimonials/${id}`);
      setDeleteConfirm(null);
      fetchTestimonials();
    } catch (error) {
      console.error('Error deleting testimonial:', error);
    }
  };

  const resetForm = () => {
    setShowForm(false);
    setImagePreview('');
    setFormData({ name: '', company: '', role: '', content: '', source: '', image_url: '', featured: true });
  };

  const handleImageUpload = async (e) => {
    const file = e.target.files[0];
    if (!file) return;
    setUploading(true);
    try {
      const uploadData = new FormData();
      uploadData.append('file', file);
      const token = localStorage.getItem('token');
      const res = await axios.post(`${API}/upload/image`, uploadData, {
        headers: { 'Content-Type': 'multipart/form-data', Authorization: `Bearer ${token}` }
      });
      setFormData(prev => ({ ...prev, image_url: res.data.url }));
      setImagePreview(`${BACKEND_URL}${res.data.url}`);
    } catch (error) {
      console.error('Erro ao enviar imagem:', error);
    }
    setUploading(false);
  };

  return (
    <AdminLayout>
      <div data-testid="admin-testimonials">
        <div className="flex items-center justify-between mb-8">
          <div>
            <h1 className="text-3xl font-bold text-white">Depoimentos</h1>
            <p className="text-gray-400">Gerencie os depoimentos de clientes</p>
          </div>
          <button onClick={() => setShowForm(true)} className="btn-primary flex items-center gap-2" data-testid="add-testimonial-btn">
            <Plus size={18} /> Novo Depoimento
          </button>
        </div>

        {/* Form Modal */}
        {showForm && (
          <div className="fixed inset-0 bg-black/80 flex items-center justify-center z-50 p-4">
            <div className="admin-card w-full max-w-lg p-8" data-testid="testimonial-form">
              <div className="flex items-center justify-between mb-6">
                <h2 className="text-xl font-bold text-white">Novo Depoimento</h2>
                <button onClick={resetForm} className="text-gray-400 hover:text-white"><X size={24} /></button>
              </div>

              <form onSubmit={handleSubmit} className="space-y-4">
                <input type="text" placeholder="Nome do cliente *" required value={formData.name}
                  onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                  className="input-dark w-full" data-testid="testimonial-name" />
                <input type="text" placeholder="Empresa *" required value={formData.company}
                  onChange={(e) => setFormData({ ...formData, company: e.target.value })}
                  className="input-dark w-full" data-testid="testimonial-company" />
                <input type="text" placeholder="Cargo (opcional)" value={formData.role}
                  onChange={(e) => setFormData({ ...formData, role: e.target.value })}
                  className="input-dark w-full" data-testid="testimonial-role" />

                <div>
                  <label className="block text-gray-400 text-sm mb-2">Origem</label>
                  <select value={formData.source}
                    onChange={(e) => setFormData({ ...formData, source: e.target.value })}
                    className="input-dark w-full" data-testid="testimonial-source">
                    <option value="">Selecione a origem</option>
                    <option value="Google Reviews">Google Reviews</option>
                    <option value="Via WhatsApp">Via WhatsApp</option>
                    <option value="Via E-mail">Via E-mail</option>
                    <option value="Presencial">Presencial</option>
                    <option value="Instagram">Instagram</option>
                    <option value="Facebook">Facebook</option>
                  </select>
                </div>

                <textarea placeholder="Depoimento *" required rows={4} value={formData.content}
                  onChange={(e) => setFormData({ ...formData, content: e.target.value })}
                  className="input-dark w-full resize-none" data-testid="testimonial-content" />

                <div>
                  <label className="block text-gray-400 text-sm mb-2">Foto do cliente (opcional)</label>
                  <div className="flex items-center gap-4">
                    <label className={`flex items-center gap-2 px-4 py-2.5 rounded-lg cursor-pointer transition-colors ${uploading ? 'bg-gray-700 text-gray-500' : 'bg-[#00A8E8]/20 text-[#00A8E8] hover:bg-[#00A8E8]/30 border border-[#00A8E8]/30'}`}>
                      <Upload size={18} />
                      <span className="text-sm font-medium">{uploading ? 'Enviando...' : 'Anexar Foto'}</span>
                      <input type="file" accept="image/*" onChange={handleImageUpload} disabled={uploading} className="hidden" data-testid="testimonial-image-upload" />
                    </label>
                    {imagePreview && (
                      <div className="relative">
                        <img src={imagePreview} alt="Preview" className="h-14 w-14 object-cover rounded-full border-2 border-gray-600" />
                        <button type="button" onClick={() => { setImagePreview(''); setFormData(prev => ({...prev, image_url: ''})); }}
                          className="absolute -top-1 -right-1 bg-red-500 rounded-full p-0.5 hover:bg-red-400">
                          <X size={12} className="text-white" />
                        </button>
                      </div>
                    )}
                  </div>
                </div>

                <div className="flex gap-4 pt-4">
                  <button type="button" onClick={resetForm} className="btn-outline flex-1">Cancelar</button>
                  <button type="submit" className="btn-primary flex-1 flex items-center justify-center gap-2" data-testid="save-testimonial-btn">
                    <Save size={18} /> Salvar
                  </button>
                </div>
              </form>
            </div>
          </div>
        )}

        {/* Testimonials Grid */}
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
          {testimonials.map((testimonial) => (
            <div key={testimonial.id} className="admin-card p-6" data-testid={`testimonial-${testimonial.id}`}>
              <p className="text-gray-300 mb-4 leading-relaxed">"{testimonial.content}"</p>
              <div className="flex items-center justify-between">
                <div className="flex items-center gap-3">
                  {testimonial.image_url && (
                    <img src={testimonial.image_url.startsWith('/') ? `${BACKEND_URL}${testimonial.image_url}` : testimonial.image_url}
                      alt={testimonial.name} className="h-10 w-10 rounded-full object-cover border border-gray-600" />
                  )}
                  <div>
                    <p className="font-semibold text-white">{testimonial.name}</p>
                    <p className="text-sm text-gray-500">{testimonial.role && `${testimonial.role} - `}{testimonial.company}</p>
                    {testimonial.source && <p className="text-xs text-[#5BC0F8] mt-0.5">{testimonial.source}</p>}
                  </div>
                </div>
                <button onClick={() => setDeleteConfirm(testimonial.id)} className="p-2 text-gray-400 hover:text-red-400 transition-colors" data-testid={`delete-testimonial-${testimonial.id}`}>
                  <Trash2 size={18} />
                </button>
              </div>
            </div>
          ))}
        </div>

        {testimonials.length === 0 && (
          <div className="text-center py-16 admin-card">
            <Users className="text-gray-600 mx-auto mb-4" size={48} />
            <p className="text-gray-400 mb-4">Nenhum depoimento cadastrado</p>
            <button onClick={() => setShowForm(true)} className="btn-primary">Adicionar primeiro depoimento</button>
          </div>
        )}

        {deleteConfirm && (
          <div className="fixed inset-0 bg-black/60 backdrop-blur-sm z-50 flex items-center justify-center p-4">
            <div className="admin-card p-6 max-w-sm w-full text-center">
              <Trash2 className="text-red-400 mx-auto mb-3" size={32} />
              <h3 className="text-white font-bold text-lg mb-2">Excluir depoimento?</h3>
              <p className="text-gray-400 text-sm mb-6">Esta ação não pode ser desfeita.</p>
              <div className="flex gap-3">
                <button onClick={() => setDeleteConfirm(null)} className="btn-outline flex-1">Cancelar</button>
                <button onClick={() => handleDelete(deleteConfirm)} className="flex-1 bg-red-500 hover:bg-red-600 text-white py-2.5 px-4 rounded-xl font-semibold transition-colors" data-testid="confirm-delete-btn">Excluir</button>
              </div>
            </div>
          </div>
        )}
      </div>
    </AdminLayout>
  );
};

/* ═══════════════════════ USERS ═══════════════════════ */
export const AdminUsers = () => {
  const [users, setUsers] = useState([]);
  const [showForm, setShowForm] = useState(false);
  const [editingUser, setEditingUser] = useState(null);
  const [formData, setFormData] = useState({ name: '', email: '', password: '' });
  const [error, setError] = useState('');
  const [deleteTarget, setDeleteTarget] = useState(null);
  const { user: currentUser } = useAuth();

  useEffect(() => { fetchUsers(); }, []);

  const fetchUsers = async () => {
    try {
      const res = await axios.get(`${API}/users`);
      setUsers(res.data);
    } catch (err) { console.error('Error fetching users:', err); }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');
    try {
      if (editingUser) {
        const payload = { name: formData.name, email: formData.email };
        if (formData.password) payload.password = formData.password;
        await axios.put(`${API}/users/${editingUser.id}`, payload);
      } else {
        if (!formData.password) { setError('Senha é obrigatória'); return; }
        await axios.post(`${API}/users`, formData);
      }
      fetchUsers();
      resetForm();
    } catch (err) {
      setError(err.response?.data?.detail || 'Erro ao salvar');
    }
  };

  const handleDelete = async (userId) => {
    try {
      await axios.delete(`${API}/users/${userId}`);
      fetchUsers();
      setDeleteTarget(null);
    } catch (err) {
      alert(err.response?.data?.detail || 'Erro ao excluir');
      setDeleteTarget(null);
    }
  };

  const startEdit = (u) => {
    setEditingUser(u);
    setFormData({ name: u.name, email: u.email, password: '' });
    setShowForm(true);
    setError('');
  };

  const resetForm = () => {
    setShowForm(false);
    setEditingUser(null);
    setFormData({ name: '', email: '', password: '' });
    setError('');
  };

  return (
    <AdminLayout>
      <div className="p-8" data-testid="admin-users">
        <div className="flex items-center justify-between mb-8">
          <div>
            <h1 className="text-2xl font-bold text-white">Usuários</h1>
            <p className="text-gray-400 text-sm mt-1">Gerencie quem tem acesso ao painel</p>
          </div>
          <button onClick={() => { resetForm(); setShowForm(true); }} className="btn-primary flex items-center gap-2" data-testid="add-user-btn">
            <UserPlus size={18} /> Novo Usuário
          </button>
        </div>

        {/* Modal */}
        {showForm && (
          <div className="fixed inset-0 bg-black/60 backdrop-blur-sm z-50 flex items-center justify-center p-4">
            <div className="admin-card w-full max-w-md p-6" data-testid="user-modal">
              <div className="flex items-center justify-between mb-6">
                <h2 className="text-lg font-bold text-white">{editingUser ? 'Editar Usuário' : 'Novo Usuário'}</h2>
                <button onClick={resetForm} className="text-gray-400 hover:text-white"><X size={20} /></button>
              </div>

              {error && <div className="bg-red-500/10 border border-red-500/20 text-red-400 text-sm rounded-lg p-3 mb-4">{error}</div>}

              <form onSubmit={handleSubmit} className="space-y-4">
                <input type="text" placeholder="Nome" required value={formData.name}
                  onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                  className="input-dark w-full" data-testid="user-name" />
                <input type="email" placeholder="Email" required value={formData.email}
                  onChange={(e) => setFormData({ ...formData, email: e.target.value })}
                  className="input-dark w-full" data-testid="user-email" />
                <div>
                  <input type="password" placeholder={editingUser ? 'Nova senha (deixe vazio para manter)' : 'Senha'}
                    value={formData.password}
                    onChange={(e) => setFormData({ ...formData, password: e.target.value })}
                    className="input-dark w-full" data-testid="user-password" />
                </div>
                <div className="flex gap-3 pt-2">
                  <button type="submit" className="btn-primary flex-1 flex items-center justify-center gap-2" data-testid="save-user-btn">
                    <Save size={16} /> {editingUser ? 'Atualizar' : 'Criar'}
                  </button>
                  <button type="button" onClick={resetForm} className="btn-outline flex-1">Cancelar</button>
                </div>
              </form>
            </div>
          </div>
        )}

        {/* Users list */}
        <div className="space-y-3">
          {users.map((u) => (
            <div key={u.id} className="admin-card p-5 flex items-center justify-between" data-testid={`user-row-${u.id}`}>
              <div className="flex items-center gap-4">
                <div className="w-10 h-10 rounded-full bg-blue-500/20 flex items-center justify-center">
                  <span className="text-blue-400 font-bold text-sm">{u.name?.charAt(0)?.toUpperCase()}</span>
                </div>
                <div>
                  <p className="text-white font-semibold">{u.name}</p>
                  <p className="text-gray-400 text-sm">{u.email}</p>
                </div>
              </div>
              <div className="flex items-center gap-2">
                <span className="text-xs bg-blue-500/10 text-blue-400 px-2 py-1 rounded">{u.role}</span>
                {u.id === currentUser?.id && <span className="text-xs bg-green-500/10 text-green-400 px-2 py-1 rounded">Você</span>}
                <button onClick={() => startEdit(u)} className="p-2 text-gray-400 hover:text-white transition-colors" data-testid={`edit-user-${u.id}`}>
                  <Edit size={16} />
                </button>
                {u.id !== currentUser?.id && (
                  <button onClick={() => setDeleteTarget(u)} className="p-2 text-gray-400 hover:text-red-400 transition-colors" data-testid={`delete-user-${u.id}`}>
                    <Trash2 size={16} />
                  </button>
                )}
              </div>
            </div>
          ))}
        </div>

        {/* Delete Confirmation Modal */}
        {deleteTarget && (
          <div className="fixed inset-0 bg-black/60 backdrop-blur-sm z-50 flex items-center justify-center p-4">
            <div className="admin-card w-full max-w-sm p-6 text-center" data-testid="delete-user-modal">
              <div className="w-14 h-14 rounded-full bg-red-500/10 flex items-center justify-center mx-auto mb-4">
                <Trash2 className="text-red-400" size={24} />
              </div>
              <h3 className="text-lg font-bold text-white mb-2">Excluir Usuário</h3>
              <p className="text-gray-400 text-sm mb-6">Tem certeza que deseja excluir <strong className="text-white">{deleteTarget.name}</strong>?</p>
              <div className="flex gap-3">
                <button onClick={() => setDeleteTarget(null)} className="btn-outline flex-1">Cancelar</button>
                <button onClick={() => handleDelete(deleteTarget.id)} className="flex-1 bg-red-600 hover:bg-red-700 text-white px-4 py-2 rounded-xl font-semibold transition-colors" data-testid="confirm-delete-user">Excluir</button>
              </div>
            </div>
          </div>
        )}
      </div>
    </AdminLayout>
  );
};


/* ═══════════════════════ SETTINGS ═══════════════════════ */
export const AdminSettings = () => {
  const [activeTab, setActiveTab] = useState('privacy');
  const [saving, setSaving] = useState(false);
  const [saved, setSaved] = useState('');

  // Privacy
  const [privacySections, setPrivacySections] = useState([]);

  // Contact form
  const [contactFields, setContactFields] = useState([]);

  // Trabalhe Conosco form
  const [trabalheFields, setTrabalheFields] = useState([]);
  const [experienciaOpcoes, setExperienciaOpcoes] = useState([]);
  const [novaOpcao, setNovaOpcao] = useState('');

  // GA4
  const [ga4Id, setGa4Id] = useState('');

  useEffect(() => {
    loadSettings();
  }, []);

  const loadSettings = async () => {
    try {
      const [privRes, contactRes, trabalheRes, expRes, ga4Res] = await Promise.all([
        axios.get(`${API}/settings/privacy_sections`),
        axios.get(`${API}/settings/contact_fields`),
        axios.get(`${API}/settings/trabalhe_fields`),
        axios.get(`${API}/settings/experiencia_opcoes`),
        axios.get(`${API}/settings/ga4_id`),
      ]);
      if (privRes.data.value) setPrivacySections(privRes.data.value);
      if (contactRes.data.value) setContactFields(contactRes.data.value);
      if (trabalheRes.data.value) setTrabalheFields(trabalheRes.data.value);
      if (expRes.data.value) setExperienciaOpcoes(expRes.data.value);
      if (ga4Res.data.value) setGa4Id(ga4Res.data.value);
    } catch (err) { console.error(err); }
  };

  const saveKey = async (key, value) => {
    setSaving(true);
    try {
      await axios.put(`${API}/settings/${key}`, { value });
      setSaved(key);
      setTimeout(() => setSaved(''), 2000);
    } catch (err) { console.error(err); }
    setSaving(false);
  };

  const moveItem = (arr, setArr, idx, dir) => {
    const newArr = [...arr];
    const target = idx + dir;
    if (target < 0 || target >= newArr.length) return;
    [newArr[idx], newArr[target]] = [newArr[target], newArr[idx]];
    setArr(newArr);
  };

  const inputCls = "w-full bg-[#0a1128] border border-white/10 text-white rounded-xl px-4 py-3 text-sm placeholder-gray-500 focus:outline-none focus:border-blue-500/50 focus:ring-1 focus:ring-blue-500/30 transition-colors";

  const tabs = [
    { id: 'privacy', label: 'Política de Privacidade' },
    { id: 'contact', label: 'Formulário Orçamento' },
    { id: 'trabalhe', label: 'Formulário Candidatos' },
    { id: 'ga4', label: 'Google Analytics' },
  ];

  return (
    <AdminLayout>
      <div className="p-8" data-testid="admin-settings">
        <h1 className="text-2xl font-bold text-white mb-2">Configurações</h1>
        <p className="text-gray-400 text-sm mb-8">Edite formulários e textos do site</p>

        {/* Tabs */}
        <div className="flex gap-2 mb-8 flex-wrap">
          {tabs.map(t => (
            <button key={t.id} onClick={() => setActiveTab(t.id)}
              className={`px-4 py-2 rounded-xl text-sm font-semibold transition-colors ${activeTab === t.id ? 'bg-blue-600 text-white' : 'bg-white/5 text-gray-400 hover:text-white'}`}
              data-testid={`tab-${t.id}`}>
              {t.label}
            </button>
          ))}
        </div>

        {/* ── Política de Privacidade ── */}
        {activeTab === 'privacy' && (
          <div className="space-y-4">
            <div className="flex items-center justify-between">
              <p className="text-gray-400 text-sm">Edite as seções da política. Cada seção tem um título e conteúdo.</p>
              <button onClick={() => setPrivacySections([...privacySections, { title: '', content: '' }])}
                className="btn-primary flex items-center gap-2 text-sm" data-testid="add-privacy-section">
                <Plus size={16} /> Nova Seção
              </button>
            </div>
            {privacySections.map((sec, i) => (
              <div key={i} className="admin-card p-5 space-y-3">
                <div className="flex items-center gap-2">
                  <span className="text-gray-500 text-xs w-6">{i + 1}.</span>
                  <input value={sec.title} placeholder="Título da seção"
                    onChange={e => { const n = [...privacySections]; n[i].title = e.target.value; setPrivacySections(n); }}
                    className={inputCls} data-testid={`privacy-title-${i}`} />
                  <button onClick={() => moveItem(privacySections, setPrivacySections, i, -1)} className="p-1 text-gray-500 hover:text-white"><ArrowUp size={16} /></button>
                  <button onClick={() => moveItem(privacySections, setPrivacySections, i, 1)} className="p-1 text-gray-500 hover:text-white"><ArrowDown size={16} /></button>
                  <button onClick={() => setPrivacySections(privacySections.filter((_, j) => j !== i))} className="p-1 text-gray-500 hover:text-red-400"><Trash2 size={16} /></button>
                </div>
                <textarea value={sec.content} placeholder="Conteúdo da seção" rows={3}
                  onChange={e => { const n = [...privacySections]; n[i].content = e.target.value; setPrivacySections(n); }}
                  className={inputCls} data-testid={`privacy-content-${i}`} />
              </div>
            ))}
            <button onClick={() => saveKey('privacy_sections', privacySections)} disabled={saving}
              className="btn-primary flex items-center gap-2" data-testid="save-privacy">
              <Save size={16} /> {saved === 'privacy_sections' ? 'Salvo!' : 'Salvar Política'}
            </button>
          </div>
        )}

        {/* ── Google Analytics ── */}
        {activeTab === 'ga4' && (
          <div className="space-y-4 max-w-xl">
            <div className="admin-card p-6 space-y-4">
              <h3 className="text-white font-bold">Google Analytics 4 (opcional)</h3>
              <p className="text-gray-400 text-sm">
                Além das estatísticas próprias do painel, você pode conectar o Google Analytics. Crie uma propriedade em analytics.google.com e cole aqui o ID de medição (formato G-XXXXXXXXXX). O script só é carregado para visitantes que aceitam todos os cookies (LGPD).
              </p>
              <input value={ga4Id} placeholder="G-XXXXXXXXXX"
                onChange={e => setGa4Id(e.target.value.trim())}
                className={inputCls} data-testid="ga4-id-input" />
              <button onClick={() => saveKey('ga4_id', ga4Id)} disabled={saving}
                className="btn-primary flex items-center gap-2" data-testid="save-ga4">
                <Save size={16} /> {saved === 'ga4_id' ? 'Salvo!' : 'Salvar ID'}
              </button>
            </div>
          </div>
        )}

        {/* ── Formulário Orçamento ── */}
        {activeTab === 'contact' && (
          <div className="space-y-4">
            <div className="flex items-center justify-between">
              <p className="text-gray-400 text-sm">Perguntas do formulário de orçamento/contato.</p>
              <button onClick={() => setContactFields([...contactFields, { label: '', placeholder: '', type: 'text', required: true }])}
                className="btn-primary flex items-center gap-2 text-sm" data-testid="add-contact-field">
                <Plus size={16} /> Nova Pergunta
              </button>
            </div>
            {contactFields.map((field, i) => (
              <div key={i} className="admin-card p-5">
                <div className="flex items-center gap-2 mb-3">
                  <span className="text-gray-500 text-xs w-6">{i + 1}.</span>
                  <input value={field.label} placeholder="Nome do campo (ex: Nome completo)"
                    onChange={e => { const n = [...contactFields]; n[i].label = e.target.value; setContactFields(n); }}
                    className={`${inputCls} flex-1`} data-testid={`contact-label-${i}`} />
                  <select value={field.type}
                    onChange={e => { const n = [...contactFields]; n[i].type = e.target.value; setContactFields(n); }}
                    className={`${inputCls} w-32`}>
                    <option value="text">Texto</option>
                    <option value="email">Email</option>
                    <option value="tel">Telefone</option>
                    <option value="textarea">Texto longo</option>
                    <option value="select">Seleção</option>
                    <option value="date">Data</option>
                    <option value="number">Número</option>
                  </select>
                  <label className="flex items-center gap-1 text-gray-400 text-xs cursor-pointer">
                    <input type="checkbox" checked={field.required}
                      onChange={e => { const n = [...contactFields]; n[i].required = e.target.checked; setContactFields(n); }} />
                    Obrig.
                  </label>
                  <button onClick={() => moveItem(contactFields, setContactFields, i, -1)} className="p-1 text-gray-500 hover:text-white"><ArrowUp size={16} /></button>
                  <button onClick={() => moveItem(contactFields, setContactFields, i, 1)} className="p-1 text-gray-500 hover:text-white"><ArrowDown size={16} /></button>
                  <button onClick={() => setContactFields(contactFields.filter((_, j) => j !== i))} className="p-1 text-gray-500 hover:text-red-400"><Trash2 size={16} /></button>
                </div>
                <input value={field.placeholder || ''} placeholder="Placeholder (texto de exemplo)"
                  onChange={e => { const n = [...contactFields]; n[i].placeholder = e.target.value; setContactFields(n); }}
                  className={inputCls} />
                {field.type === 'select' && (
                  <input value={field.options || ''} placeholder="Opções separadas por vírgula (ex: LED, Som, Iluminação)"
                    onChange={e => { const n = [...contactFields]; n[i].options = e.target.value; setContactFields(n); }}
                    className={`${inputCls} mt-2`} />
                )}
              </div>
            ))}
            <button onClick={() => saveKey('contact_fields', contactFields)} disabled={saving}
              className="btn-primary flex items-center gap-2" data-testid="save-contact">
              <Save size={16} /> {saved === 'contact_fields' ? 'Salvo!' : 'Salvar Formulário'}
            </button>
          </div>
        )}

        {/* ── Formulário Candidatos ── */}
        {activeTab === 'trabalhe' && (
          <div className="space-y-6">
            {/* Opções de experiência */}
            <div className="admin-card p-5">
              <h3 className="text-white font-bold text-sm mb-3">Opções de Experiência (tags selecionáveis)</h3>
              <div className="flex flex-wrap gap-2 mb-3">
                {experienciaOpcoes.map((op, i) => (
                  <span key={i} className="bg-blue-500/20 text-blue-400 px-3 py-1 rounded-full text-sm flex items-center gap-2">
                    {op}
                    <button onClick={() => setExperienciaOpcoes(experienciaOpcoes.filter((_, j) => j !== i))} className="hover:text-red-400"><X size={12} /></button>
                  </span>
                ))}
              </div>
              <div className="flex gap-2">
                <input value={novaOpcao} placeholder="Nova opção de experiência"
                  onChange={e => setNovaOpcao(e.target.value)}
                  onKeyDown={e => { if (e.key === 'Enter' && novaOpcao.trim()) { e.preventDefault(); setExperienciaOpcoes([...experienciaOpcoes, novaOpcao.trim()]); setNovaOpcao(''); }}}
                  className={`${inputCls} flex-1`} data-testid="new-exp-option" />
                <button onClick={() => { if (novaOpcao.trim()) { setExperienciaOpcoes([...experienciaOpcoes, novaOpcao.trim()]); setNovaOpcao(''); }}}
                  className="btn-primary px-4" data-testid="add-exp-option"><Plus size={16} /></button>
              </div>
              <button onClick={() => saveKey('experiencia_opcoes', experienciaOpcoes)} disabled={saving}
                className="btn-primary flex items-center gap-2 mt-3 text-sm" data-testid="save-exp">
                <Save size={16} /> {saved === 'experiencia_opcoes' ? 'Salvo!' : 'Salvar Opções'}
              </button>
            </div>

            {/* Perguntas */}
            <div>
              <div className="flex items-center justify-between mb-4">
                <p className="text-gray-400 text-sm">Perguntas do formulário de candidatos.</p>
                <button onClick={() => setTrabalheFields([...trabalheFields, { label: '', placeholder: '', type: 'textarea', required: true }])}
                  className="btn-primary flex items-center gap-2 text-sm" data-testid="add-trabalhe-field">
                  <Plus size={16} /> Nova Pergunta
                </button>
              </div>
              {trabalheFields.map((field, i) => (
                <div key={i} className="admin-card p-5 mb-3">
                  <div className="flex items-center gap-2 mb-3">
                    <span className="text-gray-500 text-xs w-6">{i + 1}.</span>
                    <input value={field.label} placeholder="Nome do campo"
                      onChange={e => { const n = [...trabalheFields]; n[i].label = e.target.value; setTrabalheFields(n); }}
                      className={`${inputCls} flex-1`} data-testid={`trabalhe-label-${i}`} />
                    <select value={field.type}
                      onChange={e => { const n = [...trabalheFields]; n[i].type = e.target.value; setTrabalheFields(n); }}
                      className={`${inputCls} w-32`}>
                      <option value="text">Texto</option>
                      <option value="email">Email</option>
                      <option value="tel">Telefone</option>
                      <option value="textarea">Texto longo</option>
                      <option value="select">Seleção</option>
                    </select>
                    <label className="flex items-center gap-1 text-gray-400 text-xs cursor-pointer">
                      <input type="checkbox" checked={field.required}
                        onChange={e => { const n = [...trabalheFields]; n[i].required = e.target.checked; setTrabalheFields(n); }} />
                      Obrig.
                    </label>
                    <button onClick={() => moveItem(trabalheFields, setTrabalheFields, i, -1)} className="p-1 text-gray-500 hover:text-white"><ArrowUp size={16} /></button>
                    <button onClick={() => moveItem(trabalheFields, setTrabalheFields, i, 1)} className="p-1 text-gray-500 hover:text-white"><ArrowDown size={16} /></button>
                    <button onClick={() => setTrabalheFields(trabalheFields.filter((_, j) => j !== i))} className="p-1 text-gray-500 hover:text-red-400"><Trash2 size={16} /></button>
                  </div>
                  <input value={field.placeholder || ''} placeholder="Placeholder"
                    onChange={e => { const n = [...trabalheFields]; n[i].placeholder = e.target.value; setTrabalheFields(n); }}
                    className={inputCls} />
                  {field.type === 'select' && (
                    <input value={field.options || ''} placeholder="Opções separadas por vírgula"
                      onChange={e => { const n = [...trabalheFields]; n[i].options = e.target.value; setTrabalheFields(n); }}
                      className={`${inputCls} mt-2`} />
                  )}
                </div>
              ))}
              <button onClick={() => saveKey('trabalhe_fields', trabalheFields)} disabled={saving}
                className="btn-primary flex items-center gap-2" data-testid="save-trabalhe">
                <Save size={16} /> {saved === 'trabalhe_fields' ? 'Salvo!' : 'Salvar Formulário'}
              </button>
            </div>
          </div>
        )}
      </div>
    </AdminLayout>
  );
};
